import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { tokens } from "@/art/tokens";

export interface CompanionStateMomentPayload {
    id: string;
    tier: "injured" | "fallen";
    name: string;
    role?: string | null;
    portraitSrc?: string | null;
    title: string;
    body: string;
}

interface CompanionStateMomentProps {
    moment: CompanionStateMomentPayload | null;
    onClose: () => void;
}

export function CompanionStateMoment({ moment, onClose }: CompanionStateMomentProps) {
    useEffect(() => {
        if (!moment) return;
        const timeout = window.setTimeout(onClose, moment.tier === "fallen" ? 3200 : 2600);
        return () => window.clearTimeout(timeout);
    }, [moment, onClose]);

    return (
        <AnimatePresence>
            {moment && (
                <>
                    <motion.div
                        key={`companion-scrim-${moment.id}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0"
                        style={{
                            zIndex: tokens.elevation.modal + 9,
                            background: moment.tier === "fallen"
                                ? `${tokens.color.ink}CC`
                                : `${tokens.color.ink}99`,
                            backdropFilter: "blur(4px)",
                        }}
                    />
                    <motion.div
                        key={`companion-panel-${moment.id}`}
                        initial={{ opacity: 0, y: 18, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.98 }}
                        transition={{ duration: 0.28, ease: tokens.easing.decelerate }}
                        className="fixed inset-0 flex items-center justify-center px-xl"
                        style={{ zIndex: tokens.elevation.modal + 10 }}
                    >
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full max-w-[780px] rounded-l text-left overflow-hidden"
                            style={{
                                background: `linear-gradient(180deg, ${tokens.color.ink}F2, ${tokens.color.ink}E8)`,
                                border: `1px solid ${moment.tier === "fallen" ? tokens.color.burgundy : tokens.color.ember}55`,
                                boxShadow: `0 22px 48px ${tokens.color.ink}CC, inset 0 1px 0 ${moment.tier === "fallen" ? tokens.color.burgundy : tokens.color.ember}22`,
                                cursor: "pointer",
                            }}
                        >
                            <div className="grid grid-cols-[220px,1fr] min-h-[280px]">
                                <div
                                    className="relative overflow-hidden"
                                    style={{
                                        background: `radial-gradient(circle at 50% 25%, ${moment.tier === "fallen" ? `${tokens.color.burgundy}44` : `${tokens.color.ember}44`}, transparent 68%), linear-gradient(180deg, ${tokens.color.ink}CC, ${tokens.color.ink}F2)`,
                                        borderRight: `1px solid ${tokens.color.parchmentDim}22`,
                                    }}
                                >
                                    {moment.portraitSrc ? (
                                        <img
                                            src={moment.portraitSrc}
                                            alt={moment.name}
                                            className="absolute inset-0 w-full h-full object-cover"
                                            style={{
                                                opacity: 0.9,
                                                filter: moment.tier === "fallen" ? "grayscale(18%) saturate(0.82)" : "saturate(0.94)",
                                            }}
                                        />
                                    ) : null}
                                    <div
                                        className="absolute inset-x-0 bottom-0 p-m"
                                        style={{
                                            background: "linear-gradient(180deg, transparent, rgba(5,6,10,0.94))",
                                        }}
                                    >
                                        {/* 2026-04-23: Spirytale 타이틀 스타일 통일. */}
                                        <div
                                            className="font-body"
                                            style={{
                                                color: moment.tier === "fallen" ? tokens.color.burgundy : tokens.color.ember,
                                                fontSize: 10,
                                                letterSpacing: "0.22em",
                                                textTransform: "uppercase",
                                                opacity: 0.85,
                                            }}
                                        >
                                            {moment.tier === "fallen" ? "쓰러진 이름" : "상처 입은 이름"}
                                        </div>
                                        <div
                                            className="mt-[2px] font-display"
                                            style={{
                                                color: tokens.color.parchment,
                                                fontSize: "clamp(22px, 1.7vw, 28px)",
                                                letterSpacing: "0.04em",
                                                textShadow: `0 0 14px ${moment.tier === "fallen" ? tokens.color.burgundy : tokens.color.ember}66, 0 1px 0 ${tokens.color.ink}`,
                                            }}
                                        >
                                            {moment.name}
                                        </div>
                                        {moment.role ? (
                                            <div
                                                className="mt-[2px] font-body"
                                                style={{
                                                    color: tokens.color.parchmentDim,
                                                    fontSize: tokens.type.size.tiny,
                                                    letterSpacing: "0.04em",
                                                }}
                                            >
                                                {moment.role}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>

                                <div className="flex flex-col justify-between p-xl">
                                    <div>
                                        {/* 2026-04-23: accent dash + uppercase kicker + Spirytale title. */}
                                        <div
                                            className="font-body flex items-center gap-xs"
                                            style={{
                                                color: moment.tier === "fallen" ? tokens.color.burgundy : tokens.color.ember,
                                                fontSize: 11,
                                                letterSpacing: "0.22em",
                                            }}
                                        >
                                            <span
                                                aria-hidden
                                                style={{
                                                    width: 12,
                                                    height: 1,
                                                    background: moment.tier === "fallen" ? tokens.color.burgundy : tokens.color.ember,
                                                    boxShadow: `0 0 6px ${moment.tier === "fallen" ? tokens.color.burgundy : tokens.color.ember}`,
                                                    opacity: 0.7,
                                                }}
                                            />
                                            원정의 흔들림
                                        </div>
                                        <motion.div
                                            className="mt-s font-display"
                                            initial={{ opacity: 0, letterSpacing: "0.3em" }}
                                            animate={{ opacity: 1, letterSpacing: "0.04em" }}
                                            transition={{ duration: 0.9, ease: tokens.easing.decelerate }}
                                            style={{
                                                color: tokens.color.parchment,
                                                fontSize: "clamp(24px, 2vw, 32px)",
                                                lineHeight: 1.2,
                                                textShadow: `0 0 22px ${moment.tier === "fallen" ? tokens.color.burgundy : tokens.color.ember}66, 0 2px 0 ${tokens.color.ink}`,
                                            }}
                                        >
                                            {moment.title}
                                        </motion.div>
                                        <div
                                            className="mt-m font-body"
                                            style={{
                                                color: tokens.color.parchment,
                                                fontSize: 17,
                                                lineHeight: 1.85,
                                                whiteSpace: "pre-line",
                                                opacity: 0.92,
                                            }}
                                        >
                                            {moment.body}
                                        </div>
                                    </div>

                                    <div
                                        className="mt-l font-body"
                                        style={{
                                            color: tokens.color.parchmentDim,
                                            fontSize: tokens.type.size.tiny,
                                            letterSpacing: tokens.type.letterSpacing.wide,
                                        }}
                                    >
                                        클릭하거나 잠시 기다리면 장면이 이어집니다
                                    </div>
                                </div>
                            </div>
                        </button>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
